import { useEffect } from "react";
import { useLocation } from "wouter";
import { analyticsAllowed } from "./analytics-policy";
import { installAnalytics } from "./track";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

export function useMetaPixelPageViews() {
  const [location] = useLocation();
  useEffect(() => {
    if (!analyticsAllowed()) return;
    installAnalytics();
    if (!window.fbq) {
      window.__kitdexMarketingLoaded = true;
      const queue = ((...args: unknown[]) => {
        if (!analyticsAllowed()) return;
        queue.callMethod ? queue.callMethod(...args) : queue.queue.push(args);
      }) as ((...args: unknown[]) => void) & {
        callMethod?: (...args: unknown[]) => void; queue: unknown[][]; push?: unknown;
        loaded: boolean; version: string;
      };
      queue.queue = [];
      queue.push = queue;
      queue.loaded = true;
      queue.version = "2.0";
      window.fbq = queue;
      const script = document.createElement("script");
      script.async = true;
      script.src = "https://connect.facebook.net/en_US/fbevents.js";
      document.head.appendChild(script);
      queue("set", "autoConfig", false, "2791327147915378");
      queue("init", "2791327147915378");
    }
    window.fbq("track", "PageView");
  }, [location]);
}

function fbq(...args: unknown[]) {
  try {
    if (typeof window !== "undefined" && analyticsAllowed() && typeof window.fbq === "function") {
      window.fbq(...args);
    }
  } catch {
    // Analytics must never break the app.
  }
}

/** Standard Meta Pixel "Search" event — fired on search submit. */
export function trackPixelSearch(query: string) {
  fbq("track", "Search", { search_string: query });
}

/** Standard Meta Pixel "ViewContent" event — fired when a shirt detail page loads. */
export function trackPixelViewContent(params: {
  slug: string;
  title?: string;
  club?: string | null;
}) {
  fbq("track", "ViewContent", {
    content_ids: [params.slug],
    content_type: "product",
    content_name: params.title ?? params.slug,
    content_category: params.club ?? undefined,
  });
}

/** Custom Meta Pixel event — fired when a user clicks through to a retailer listing. */
export function trackPixelRetailerClick(params: {
  retailer?: string | null;
  slug?: string;
  price?: number;
  currency?: string;
}) {
  fbq("trackCustom", "RetailerClick", {
    retailer: params.retailer ?? undefined,
    content_ids: params.slug ? [params.slug] : undefined,
    value: params.price,
    currency: params.currency,
  });
}
